/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { action, computed, get } from '@ember/object';
import { service } from '@ember/service';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import Alphabet from 'linced-app/enums/alphabet';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import ENV from 'linced-app/config/environment';
import { findBy, uniq } from '@linc-technologies/linced-common/utils/arrays';

export default class ApplyLedgerItemDialog extends Component {
  // Services
  @service
  features;

  @service
  fetch;

  @service
  paperToaster;

  @service
  session;

  @service
  store;

  @service
  tax;

  EnrolmentStatus = EnrolmentStatus;

  @tracked
  applyInProgress = false;

  @tracked
  currentStep = 0;

  @action
  setCurrentStep(value) {
    this.currentStep = value;
  }

  @tracked
  includeGroups = [];

  @tracked
  excludeGroups = [];

  @tracked
  selectedYearLevels = [];

  @tracked
  selectedPositions = [];

  @tracked
  description = '';

  @action
  setDescription(value) {
    this.description = value;
  }

  @tracked
  date = '';

  @action
  setDate(value) {
    this.date = value;
  }

  @tracked
  amount = 0;

  @action
  setAmount(value) {
    this.amount = value;
  }

  @tracked
  descriptionViolations = [];

  @action
  setDescriptionViolations(value) {
    this.descriptionViolations = value;
  }

  @computed('descriptionViolations.[]')
  get hasLanguageViolations() {
    return !isEmpty(this.descriptionViolations);
  }

  @alias('session.school.jurisdiction')
  schoolJurisdiction;

  Jurisdiction = Jurisdiction;

  @tracked
  learnersWithLineItem = [];

  @tracked
  bulkSelectedLearners = [];

  @action
  setBulkSelectedLearners(value) {
    this.bulkSelectedLearners = value;
  }

  @tracked
  selectedLearners = [];

  @alias('selectedLearners.length')
  numSelected;

  alphabet = Alphabet.values;

  @action
  handleToggleApplyItemDialog(evt) {
    // The action passed in to this component accepts the Ledger Item as its first argument, if opening the dialog.
    // When closing, which is all we can do from inside this component, it should be null to avoid resetting it early.
    this.toggleApplyItemDialog(null, evt);
  }

  @action
  apply() {
    this.applyInProgress = true;
    let promise = Promise.resolve();

    // If any of the details were changed from the defaults, update the Ledger Item with the changes first.
    this.ledgerItem.setProperties({
      defaultDescription: this.description,
      defaultDate: this.date,
      defaultAmount: Number(this.amount),
    });
    if (this.ledgerItem.hasDirtyAttributes) {
      promise = this.ledgerItem.save();
    }

    promise.then(() => {
      let url = `${ENV.APP.apiHost}/finance/${ENV.APP.apiPath}/ledgeritems/${this.ledgerItem.id}/:apply`;
      let taxType = this.get('ledgerItem.ledger.taxType');
      let data = {
        personIds: this.selectedLearners,
        taxPercentage: this.tax.getRate(this.ledgerItem.date, taxType) || 0,
      };
      let options = {
        data: JSON.stringify(data),
      };
      this.fetch
        .post(url, options)
        .then((response) => {
          this.paperToaster.show(
            `${response.lineItems.length} Line Item(s) created successfully.`,
            {
              duration: 4000,
              position: 'top right',
              toastClass: 'success',
            },
          );
        })
        .catch(() => {
          this.paperToaster.show('Error creating Line Items!', {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          });
        })
        .finally(() => {
          this.applyInProgress = false;
        });
    });
  }

  @action
  toggleGroup(listType, group) {
    if (listType !== 'include' && listType !== 'exclude') {
      return;
    }
    let field = `${listType}Groups`;
    let selected = this.get(field);
    if (findBy(selected, 'id', group.id)) {
      this.set(
        field,
        selected.filter((g) => g !== group),
      );
    } else {
      this.set(field, [...selected, group]);
    }
  }

  @action
  toggleYearLevel(yearLevel) {
    if (this.selectedYearLevels.includes(yearLevel)) {
      this.selectedYearLevels = this.selectedYearLevels.filter(
        (level) => level !== yearLevel,
      );
    } else {
      this.selectedYearLevels = [...this.selectedYearLevels, yearLevel];
    }
  }

  @action
  toggleByPosition(position) {
    if (this.selectedPositions.includes(position)) {
      this.selectedPositions = this.selectedPositions.filter(
        (p) => p !== position,
      );
    } else {
      this.selectedPositions = [...this.selectedPositions, position];
    }
  }

  @action
  toggleLearner(learner) {
    if (this.selectedLearners.includes(learner.id)) {
      this.selectedLearners = this.selectedLearners.filter(
        (id) => id !== learner.id,
      );
    } else {
      this.selectedLearners = [...this.selectedLearners, learner.id];
    }
  }

  @action
  selectAll() {
    this.selectedLearners = this.learners.map((learner) => learner.id);
  }

  @action
  deselectAll() {
    this.selectedLearners = [];
  }

  @action
  deselectNzMoeInternational() {
    let learners = this.learners.filter(
      (learner) => get(learner, 'fields.nzMOE:type.value') === 'FF',
    );
    let excludeIds = learners.map((learner) => learner.id);
    this.selectedLearners = this.selectedLearners.filter(
      (id) => !excludeIds.includes(id),
    );
  }

  @action
  applyBulkSelection() {
    this.selectedLearners = this.bulkSelectedLearners.slice();
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    this.currentStep = 0;
    this.includeGroups = [];
    this.excludeGroups = [];
    this.selectedYearLevels = [];
    this.selectedPositions = [];
    this.description = this.get('ledgerItem.defaultDescription');
    this.descriptionViolations = [];
    this.date = this.get('ledgerItem.defaultDate');
    this.amount = Number(this.get('ledgerItem.defaultAmount')).toFixed(2);
    this.selectedLearners = [];
    this.learnersWithLineItem = [];

    this.store
      .query('finance/line-item', { ledgerItemId: this.ledgerItem.id })
      .then((items) => {
        this.learnersWithLineItem = uniq(
          items.map((item) => get(item, 'account.person.id')),
        );
      });
  }
}
